import React, { useEffect, useRef } from "react";

const PlanConfigModal = ({ plan, open, onClose }) => {
  const dialogRef = useRef(null);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, [open]);

  if (!open || !plan) return null;

  const planName =
    typeof plan.name === "string" && plan.name.trim()
      ? plan.name
      : "Plan Details";

  const planHtml =
    typeof plan?.planConfig?.planHtml === "string"
      ? plan.planConfig.planHtml
      : "";

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-3 sm:px-5"
      role="dialog"
      aria-modal="true"
      aria-labelledby="plan-config-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm transition-opacity duration-300"
        onClick={onClose}
      />

      {/* Dialog */}
      <div
        ref={dialogRef}
        className="relative w-full max-w-2xl max-h-[85vh] bg-white rounded-3xl shadow-2xl flex flex-col overflow-hidden animate-modal-in"
      >
        {/* Sticky header */}
        <div className="sticky top-0 z-10 flex items-start justify-between gap-4 px-5 py-4 bg-white border-b border-[#EFEDF4]">
          <div className="min-w-0">
            <p className="text-[11px] font-bold uppercase tracking-widest text-[#6D28D9] mb-0.5">
              Plan Benefits
            </p>
            <h3
              id="plan-config-title"
              className="text-xl sm:text-2xl font-bold text-[#14131A] truncate"
            >
              {planName}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 h-9 w-9 rounded-full flex items-center justify-center text-[#8A8798] hover:text-[#6D28D9] hover:bg-[#F7F5FB] transition-colors"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path
                d="M2 2l14 14M16 2L2 16"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div className="overflow-y-auto px-5 sm:px-7 py-5 sm:py-6">
          {planHtml ? (
            <div
              className="plan-html-content text-slate-700"
              dangerouslySetInnerHTML={{ __html: planHtml }}
            />
          ) : (
            <p className="text-sm text-[#8A8798] italic text-center py-8">
              No plan details available yet.
            </p>
          )}
        </div>
      </div>

      <style>{`
        @keyframes modal-in {
          from { opacity: 0; transform: translateY(12px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .animate-modal-in {
          animation: modal-in 220ms ease-out;
        }

        /* Responsive styling for the injected planHtml markup */
        .plan-html-content p {
          margin: 0 0 10px 0;
          font-size: 14px;
          line-height: 1.6;
        }
        .plan-html-content strong {
          color: #14131a;
          font-weight: 700;
        }
        .plan-html-content ul {
          list-style: none;
          padding-left: 0;
          margin: 0 0 16px 0;
        }
        .plan-html-content li {
          font-size: 14px;
          line-height: 1.7;
          padding-left: 1.5em;
          text-indent: -1.5em;
          word-break: break-word;
        }
        @media (max-width: 480px) {
          .plan-html-content li,
          .plan-html-content p {
            font-size: 13px;
          }
        }
      `}</style>
    </div>
  );
};

export default PlanConfigModal;